import React, { useState, useEffect, useRef } from 'react';
import ProgressMessage from 'components/Utils/ProgressMessage';
import Icon from 'components/Utils/NewIcon';
import RecordRTC from 'recordrtc';
import DefaultButton from 'components/Utils/NewDefaultButton';
import moment from 'moment-timezone';
import './style.scss';
import MiscUtils from 'services/MiscUtils';
import CalendarUtils from 'services/CalendarUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import KeyHandler from 'react-key-handler';
import shutterSound from 'assets/audio/shutter.mp3';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';

const OTOSCOPE_LABEL = 'Digital Microscope';
const RECORDING_METADATA = {
  video: {
    mime: 'video/mp4',
    recordMime: 'video/webm;codecs=h264',
    extension: 'mp4',
    description: 'Otoscope Video',
  },
  photo: {
    mime: 'image/png',
    extension: 'png',
    description: 'Otoscope Photo',
  },
};
const WIDTH = 1280;
const HEIGHT = 1024;

const OtoscopeScreen = ({ patient, onClose }) => {
  const dispatch = useAppDispatch();

  const [label, setLabel] = useState(OTOSCOPE_LABEL);
  const [otoscopeDevice, setOtoscopeDevice] = useState();
  const [devices, setDevices] = useState([]);
  const [recorder, setRecorder] = useState();
  const [videoStream, setVideoStream] = useState(undefined);
  const [attachments, setAttachments] = useState([]);
  const [doneAttachments, setDoneAttachments] = useState([]);
  const [editModal, setEditModal] = useState();

  const refVideo = useRef();

  const isRecording = !!recorder;
  const connected = !!videoStream;

  useEffect(() => {
    let timeout;

    async function getPermissions() {
      const constraints = { video: true };
      let stream = await navigator.mediaDevices.getUserMedia(constraints);
      stopVideo(stream);
    }

    async function findDevices() {
      let devices = await navigator.mediaDevices.enumerateDevices();

      devices = devices.filter((device) => device.kind === 'videoinput');

      if (!devices[0].label) {
        await getPermissions();
        return findDevices();
      }

      setDevices(devices);
      let otoscope = devices.find((device) => device.label.includes(label));
      setOtoscopeDevice(otoscope);
      if (!otoscope) {
        timeout = setTimeout(findDevices, 1000);
      }
    }

    if (!otoscopeDevice) findDevices();

    return () => {
      clearTimeout(timeout);
    };
  }, [label, otoscopeDevice]);

  useEffect(() => {
    if (otoscopeDevice) {
      startVideo(otoscopeDevice);
      MiscUtils.showToastrInfo(
        {
          title: 'Photo Shortcut Tip',
          message: 'Press the Spacebar to quickly take a photo.',
        },
        {
          timeOut: 3 * 1000,
          showCloseButton: false,
          closeOnToastrClick: false,
        },
      );
    }
  }, [otoscopeDevice]);

  useEffect(() => {
    let madeChanges = false;

    let newAttachments = [...attachments].map((attachment) => {
      let isNowDone = doneAttachments.find((filename) => attachment.filename === filename);
      if (attachment.loading && !!isNowDone) {
        attachment.loading = false;
        madeChanges = true;
      }
      return attachment;
    });

    if (madeChanges) {
      setAttachments(newAttachments);
    }
  }, [doneAttachments, attachments]);

  function stopVideo(stream = videoStream) {
    if (stream) stream.getTracks().forEach((track) => track.stop());
  }

  async function closeWindow() {
    await stopRecording();
    stopVideo(videoStream);
    if (onClose) onClose();
  }

  async function startVideo(device) {
    let stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: device.id, width: { exact: WIDTH }, height: { exact: HEIGHT } },
      audio: false,
    });

    refVideo.current.srcObject = stream;

    setVideoStream(stream);
  }

  async function takePhoto() {
    let video = refVideo.current;
    const canvas = document.createElement('canvas');
    canvas.width = WIDTH;
    canvas.height = HEIGHT;
    canvas.getContext('2d').drawImage(video, 0, 0, WIDTH, HEIGHT);
    MiscUtils.playAudio(shutterSound);

    canvas.toBlob(async (blob) => {
      let metadata = RECORDING_METADATA.photo;
      let filename = `otoscope ${CalendarUtils.format(moment().unix(), 'DD/MM/YYYY h:mm:ss A')}.${
        metadata.extension
      }`;
      const result = await saveBlob(blob, metadata, filename);
      if (result) {
        setDoneAttachments((prevState) => {
          return [...prevState, filename];
        });
      }
    });
  }

  async function saveBlob(blob, metadata, filename) {
    let file = new File([blob], filename, {
      type: metadata.mime,
    });
    let blobUrl = MiscUtils.getBlobUrl(blob);
    setAttachments([
      {
        metadata,
        blob,
        loading: true,
        filename,
        blobUrl,
      },
      ...attachments,
    ]);

    return dispatch(
      api.HealthRecords.addNotesAttachment({
        patientId: patient.id,
        file,
        notes: metadata.description,
        hide: true,
      }),
      {
        errorTitle: metadata.description,
      },
    );
  }

  async function record() {
    if (videoStream && !isRecording) {
      let recorder = RecordRTC(videoStream, {
        type: 'video',
        mimeType: RECORDING_METADATA.video.recordMime,
        frameInterval: 30,
      });
      recorder.startRecording();
      setRecorder(recorder);
    }
  }

  async function stopRecording() {
    if (!!recorder) {
      return new Promise((resolve, reject) => {
        recorder.stopRecording(async () => {
          let blob = recorder.getBlob();
          setRecorder(undefined);
          let metadata = RECORDING_METADATA.video;
          let filename = `otoscope ${CalendarUtils.format(
            moment().unix(),
            'DD/MM/YYYY h:mm:ss A',
          )}.${metadata.extension}`;
          const result = await saveBlob(blob, metadata, filename);
          if (result) {
            setDoneAttachments((prevState) => {
              return [...prevState, filename];
            });
            return resolve();
          }
          return reject();
        });
      });
    }
  }

  async function editName(attachment, name) {
    let updated = {
      ...attachment,
      loading: true,
      note: {
        ...attachment.note,
        value: {
          ...attachment.note.value,
          notes: name,
        },
      },
    };
    const newAttachments = PerformanceUtils.editOrAdd(updated, attachments, 'filename');
    setAttachments(newAttachments);
    setEditModal();
    const result = await dispatch(
      api.HealthRecords.editNote({ id: updated.note.id, appointmentNote: updated.note }),
      { errorTitle: 'Update Media Name' },
    );
    if (!result) {
      const newAttachments = PerformanceUtils.editOrAdd(attachment, attachments, 'filename');
      setAttachments(newAttachments);
    }
  }

  return (
    <div className="otoscope-screen">
      <KeyHandler keyValue=" " onKeyHandle={takePhoto} />
      {!!editModal && (
        <ConfirmationDialog
          title="Change Media Name"
          open={!!editModal}
          message="Set the new name for the photo or video"
          onConfirm={(event, form) => editName(editModal, form['Name'])}
          form={[{ type: 'textField', label: 'Name' }]}
          onCancel={() => setEditModal()}
          loadingState={false}
        />
      )}
      <div className="top">
        <span className="large-title">Otoscope</span>
        <Icon className="close-icon" src="close-icon" onClick={closeWindow} height="25px" />
      </div>
      <div className="otoscope-content row">
        {connected && (
          <div className="photos-column underside-right-panel full-height scroll-list">
            <div className="buttons">
              <DefaultButton
                icon="camera"
                name="Take Photo"
                style={{ width: '180px' }}
                onClick={takePhoto}
              />
              <DefaultButton
                icon="video"
                name={isRecording ? 'Stop Recording' : 'Record Video'}
                style={{ width: '180px' }}
                onClick={isRecording ? stopRecording : record}
                className={isRecording ? 'red' : undefined}
              />
            </div>
            {attachments.map((attachment) => {
              let { metadata } = attachment;
              let { photo, video } = RECORDING_METADATA;
              let className = 'attachment';
              if (attachment.loading) className += ' loading';
              return (
                <div className={className} key={attachment.filename}>
                  {metadata.mime === photo.mime && (
                    <>
                      <img src={attachment.blobUrl} alt={attachment.filename} />
                      <Icon
                        src="image"
                        className="white"
                        width="20px"
                        alt="Photo"
                        showTooltip={true}
                      />
                    </>
                  )}
                  {metadata.mime === video.mime && (
                    <>
                      <video src={attachment.blobUrl} autoPlay loop />
                      <Icon
                        src="video"
                        className="white"
                        width="20px"
                        alt="Video"
                        showTooltip={true}
                      />
                    </>
                  )}
                  {!!attachment.note && (
                    <span className="comment truncate" onClick={() => setEditModal(attachment)}>
                      {((attachment.note || {}).value || {}).notes}
                    </span>
                  )}
                  <ProgressMessage show={attachment.loading} size={30} />
                </div>
              );
            })}
          </div>
        )}
        {!otoscopeDevice && (
          <div className="not-found full-width ">
            <span>Otoscope not found. Please make sure it is plugged-in and turn on.</span>
            <span>Devices found:</span>
            <ul>
              {devices.map((device, index) => (
                <li onClick={() => setLabel(device.label)} key={device.label || index}>
                  {device.label}
                </li>
              ))}
            </ul>
          </div>
        )}
        <ProgressMessage
          show={!!otoscopeDevice && !connected}
          size={70}
          message="Connecting to Otoscope..."
          style={{ padding: '50px' }}
        />
        <div className="row full-width video-content">
          <video ref={refVideo} autoPlay>
            Video not supported by your browser
          </video>
        </div>
      </div>
    </div>
  );
};

export default OtoscopeScreen;
